import { vesselCardStyles } from "../constants/vessel-card-styles.constants";
import { VesselCardPerformancePanelProps } from "../models/vessel-card.models";
import { vesselCardZnViewAtom } from "../state/vessel-card.atoms";
import { formatVesselCardMetric } from "../utils/vessel-card-baltic.utils";
import VesselCardBalticPanel from "./VesselCardBalticPanel";
import VesselCardPerformanceGraph from "./VesselCardPerformanceGraph";
import VesselCardZnPanel from "./VesselCardZnPanel";
import { useAtom } from "jotai";
import { twMerge } from "tailwind-merge";

const indicatorClassNames = {
  positive: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  neutral: "text-teal-700 dark:text-teal-300",
  negative: "text-red-600 dark:text-red-400",
};

// Switches comparisons without changing the selected vessel or CP inputs.
// Baltic benchmark values and calculations arrive prepared by the vessel-card hook.
// Receives expected figures already corrected for the selected design fuel grade, shared across all comparisons.
const VesselCardPerformancePanel = ({
  imo,
  rows,
  cpLabel = "IMOS CP Desc",
  view,
  onViewChange,
  comparison,
  onComparisonChange,
  balticRows,
  balticIndex,
  onBalticIndexChange,
  balticBenchmarkAvailable,
  fuelGrade,
}: VesselCardPerformancePanelProps) => {
  const [znView, setZnView] = useAtom(vesselCardZnViewAtom);
  const activeView = comparison === "zn" ? znView : view;

  return (
    <section data-test="vessel-card-performance-comparison" className={twMerge(vesselCardStyles.surface, "min-w-0")}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className={vesselCardStyles.sectionTitle}>Performance comparison</h2>
        <div
          className="flex rounded-md border border-gray-300 bg-gray-50 p-1 dark:border-gray-600 dark:bg-gray-900/50"
          data-test="vessel-card-view-switch"
        >
          {(["table", "graph"] as const).map((option) => (
            <button
              key={option}
              type="button"
              data-test={`vessel-card-${option}-view-button`}
              aria-pressed={activeView === option}
              onClick={() => (comparison === "zn" ? setZnView(option) : onViewChange(option))}
              className={twMerge(
                "rounded px-3 py-1.5 text-xs font-semibold capitalize transition-colors",
                activeView === option
                  ? "bg-green text-white shadow-sm"
                  : "text-gray-600 hover:text-green dark:text-gray-300",
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <nav
        aria-label="Performance comparison"
        className="mb-4 flex flex-wrap gap-1 rounded-md border border-gray-200 bg-gray-50 p-1.5 dark:border-gray-700 dark:bg-gray-900/50"
        data-test="vessel-card-comparison-switch"
      >
        <button
          type="button"
          aria-pressed={comparison === "cp"}
          data-test="vessel-card-cp-comparison-button"
          onClick={() => onComparisonChange("cp")}
          className={twMerge(
            "rounded px-3 py-2 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-green",
            comparison === "cp" ? "bg-green text-white shadow-sm" : "text-gray-600 hover:text-green dark:text-gray-300",
          )}
        >
          Expected Perf vs CP Desc
        </button>
        <button
          type="button"
          aria-pressed={comparison === "baltic"}
          data-test="vessel-card-baltic-comparison-button"
          onClick={() => onComparisonChange("baltic")}
          className={twMerge(
            "rounded px-3 py-2 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-green",
            comparison === "baltic"
              ? "bg-green text-white shadow-sm"
              : "text-gray-600 hover:text-green dark:text-gray-300",
          )}
        >
          Expected Perf vs Baltic Ship
        </button>
        <button
          type="button"
          aria-pressed={comparison === "zn"}
          data-test="vessel-card-zn-comparison-button"
          onClick={() => onComparisonChange("zn")}
          className={twMerge(
            "rounded px-3 py-2 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-green",
            comparison === "zn" ? "bg-green text-white shadow-sm" : "text-gray-600 hover:text-green dark:text-gray-300",
          )}
        >
          Expected vs ZN Model
        </button>
      </nav>

      {comparison === "baltic" ? (
        <VesselCardBalticPanel
          rows={balticRows}
          index={balticIndex}
          onIndexChange={onBalticIndexChange}
          benchmarkAvailable={balticBenchmarkAvailable}
          fuelGrade={fuelGrade}
          view={view}
        />
      ) : comparison === "zn" ? (
        <VesselCardZnPanel imo={imo} performance={rows} view={znView} fuelGrade={fuelGrade} />
      ) : view === "table" ? (
        <div className="overflow-x-auto" data-test="vessel-card-performance-table">
          <table className="w-full min-w-[740px] border-separate border-spacing-0 text-sm">
            <thead className="text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th rowSpan={2} className="pb-2 pr-3 text-left font-medium">
                  <span className="block">Mode</span>
                  <span
                    className="mt-1 block text-amber-600 dark:text-amber-400"
                    data-test="vessel-card-cp-fuel-grade"
                    aria-live="polite"
                  >
                    Fuel grade: {fuelGrade ?? "Not available"}
                  </span>
                </th>
                <th
                  colSpan={2}
                  title="Estimated clean-hull performance at the vessel's minimum and maximum consumption limits."
                  className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                >
                  Expected performance
                </th>
                <th
                  colSpan={2}
                  className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                >
                  {cpLabel}
                </th>
                <th
                  colSpan={2}
                  title="Expected clean-hull performance calculated at the CP consumption value."
                  className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                >
                  Expected @ CP cons
                </th>
                <th
                  rowSpan={2}
                  title="Shows how expected performance compares with the selected CP description."
                  className="border-l border-gray-200 px-3 pb-2 text-left font-medium dark:border-gray-700"
                >
                  CP perf indicator
                </th>
              </tr>
              <tr>
                {Array.from({ length: 3 }).flatMap((_, groupIndex) =>
                  ["Speed", "Cons"].map((label) => (
                    <th
                      key={`${groupIndex}-${label}`}
                      className={twMerge(
                        "px-2 pb-2 text-center font-medium",
                        label === "Speed" && "border-l border-gray-200 dark:border-gray-700",
                      )}
                    >
                      {label}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.mode}
                  data-test={`vessel-card-performance-row-${row.mode.toLowerCase().replaceAll(" ", "-")}`}
                >
                  <th className="whitespace-nowrap py-3 pr-3 text-left text-sm font-semibold">{row.mode}</th>
                  {[
                    row.expectedSpeed,
                    row.expectedConsumption,
                    row.cpSpeed,
                    row.cpConsumption,
                    row.expectedAtCpSpeed,
                    row.expectedAtCpConsumption,
                  ].map((value, index) => (
                    <td
                      key={index}
                      className={twMerge(
                        "px-2 py-3 text-center",
                        index % 2 === 0 && "border-l border-gray-200 dark:border-gray-700",
                      )}
                    >
                      {formatVesselCardMetric(value)}
                    </td>
                  ))}
                  <td
                    className={twMerge(
                      "border-l border-gray-200 px-3 py-3 text-xs font-semibold dark:border-gray-700",
                      indicatorClassNames[row.indicatorTone],
                    )}
                  >
                    {row.indicator}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <VesselCardPerformanceGraph
          rows={rows.map((row) => ({ ...row, comparisonSpeed: row.cpSpeed, comparisonConsumption: row.cpConsumption }))}
          comparison="cp"
          sourceLabel={cpLabel}
          fuelGrade={fuelGrade}
        />
      )}
    </section>
  );
};

export default VesselCardPerformancePanel;
